import Head from "next/head";
import { LanguageProvider } from "../utils/language";
import { AppProvider } from "../context/AppContext";
import "../style.scss";
import { AppProps } from "next/app";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { SearchProvider } from "../context/SearchContext";
import { initAxe } from "../utils/initAxe";
import ReactLenis from "lenis/react";
import { ScriptInjection } from "@aleksundshantu.com/aunds-cookie-plugin-react";
import { gridUIConfig } from "@/ui/gridUIConfig";
import { GridUIProvider } from "@aleksundshantu.com/aunds-utils-react/primitives/grid";
import { useGoogleOff } from "@/hooks/useGoogleOff";

function MyApp({ Component, pageProps }: AppProps) {
  useEffect(() => {
    initAxe().catch((error) => {
      console.error(error);
    });
  }, []);

  // comment this out to enable google crawling for NEXT_DATA
  useGoogleOff("__NEXT_DATA__");

  const router = useRouter();
  const browserNavigation = useRef(false); //e.g. browser back button was used
  const [pageTransition, setPageTransition] = useState(false);

  const handleRouteChangeStart = () => {
    setPageTransition(true);
  };

  const handleRouteChange = (
    url: string,
    { shallow }: { shallow?: boolean } = {},
  ) => {
    if (!shallow && !browserNavigation.current) {
      document.getElementById("pagestart")?.focus();
      document.getElementById("pagestart")?.blur();
    }
    browserNavigation.current = false;
    setPageTransition(false);
    try {
      // hack for Google Analytics to track page transitions
      let gaKey: string | null = null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const winAny = window as any;

      // Check gaData (for Universal Analytics)
      if (winAny?.gaData) {
        gaKey = Object.keys(winAny.gaData)[0];
      }

      // Check dataLayer (for GA4 or GTM)
      if (!gaKey && Array.isArray(winAny?.dataLayer)) {
        const gtmConfig = winAny.dataLayer.find?.(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (entry: any) => Array.isArray(entry) && entry[0] === "config",
        );
        if (gtmConfig?.length > 1) gaKey = gtmConfig[1]; // Extract tracking ID
      }

      if (gaKey) {
        winAny?.gtag?.("config", gaKey, {
          page_path: url,
        });
      }
      // GTM dataLayer
      winAny?.dataLayer?.push?.({ event: "pageview", page: url });

      // hack for Matomo to track page transitions
      winAny?._paq?.push?.(["setCustomUrl", url]);
      winAny?._paq?.push?.(["setDocumentTitle", document.title]);
      winAny?._paq?.push?.(["trackPageView"]);

      // hack for Facebook Pixel to track page transitions
      winAny?.fbq?.("track", "PageView");
      // Pixie
      winAny?.pixie?.("event", "PageView");
      // LINKEDIN
      winAny?.lintrk?.("track");
      // TIKTOK
      winAny?.ttq?.page?.();
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    router?.beforePopState(() => {
      browserNavigation.current = true;
      return true;
    });

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("routeChangeStart", handleRouteChangeStart);
    };
  }, [router, router.events]);

  function updateScrollbarWidth() {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      `${scrollbarWidth}px`,
    );
  }

  useEffect(() => {
    window.addEventListener("resize", updateScrollbarWidth);
    updateScrollbarWidth();
    return () => {
      window.removeEventListener("resize", updateScrollbarWidth);
    };
  }, []);

  return (
    <>
      <LanguageProvider>
        <Head>
          {/* you can add metadata here, for all pages */}
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />

          {/*
          //! This is the solution for removing framer/motion animations.
          */}
          <noscript>
            {/* eslint-disable-next-line @next/next/no-css-tags */}
            <link rel="stylesheet" href="/no-js.css" />
            {/*
               To test the updated noscript CSS after making changes in no-js.css:
               Build the project and run yarn start instead of yarn dev.
               This will allow you to see the applied changes.
            */}
          </noscript>
        </Head>
        <ReactLenis
          options={{
            lerp: pageTransition ? 0 : 0.1,
          }}
          className="wrapper"
          root
        >
          <AppProvider>
            <SearchProvider>
              <GridUIProvider value={gridUIConfig}>
                <Component {...pageProps} />
              </GridUIProvider>
              <ScriptInjection />
            </SearchProvider>
          </AppProvider>
        </ReactLenis>
      </LanguageProvider>
    </>
  );
}

export default MyApp;
